import { format, getDate, getMonth, getYear } from 'date-fns';
import { isEmpty, toNumber } from 'lodash';
import { func, shape, string } from 'prop-types';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DatePicker, FormInput, Loader, RadioGroup } from 'components';
import { getValidDate, processString } from 'helpers';
import { useCivility, useFetch, useSafeState, useUser } from 'hooks';

const AddParticipantForm = ({ passengerCategory, onAddSuccess }) => {
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const { call: findParticipantCall, loading: findParticipantLoading, data: participantBySerialNumber } = useFetch();
  const { call: addParticipantCall, loading: addParticipantLoading } = useFetch();

  const [hasSearchParticipant, setHasSearchParticipant] = useSafeState(false);
  const [invalidParticipantAge, setInvalidParticipantAge] = useSafeState(null);
  const storedReturnDate = useSelector((state) => state.travelPurchase.returnDate);
  const { civilityOptions } = useCivility();

  const participantForm = useForm({
    mode: 'onBlur',
    defaultValues: {
      serialNumber: '',
      firstName: '',
      lastName: '',
      civility: '',
      birthDate: null
    }
  });

  /* Watch the value of serialNumber in the form to enable/disable search button */
  const searchParticipant = participantForm.watch('serialNumber');

  const findParticipantBySerialNumber = async () => {
    let serialNumber = participantForm.getValues('serialNumber');

    // Remove all spaces from serialNumber
    serialNumber = serialNumber.replace(/\s/g, '');

    if (isEmpty(serialNumber)) return;

    // Process the serialNumber to uppercase
    serialNumber = processString(serialNumber);

    try {
      setHasSearchParticipant(false);
      const { data } =
        (await findParticipantCall({
          service: 'castelis',
          path: `/users/rightHolders/${currentUser.serialNumber}/find/${serialNumber}`,
          method: 'get',
          // TODO : API doesn't accept Content-Type header as JSON, remove when API is fixed
          headers: {
            'Content-Type': null
          }
        })) || {};
      const participant = data?.body?.serialNumber ? data.body : null;

      if (!isEmpty(participant)) {
        const formStateOptions = { shouldValidate: true, shouldDirty: true, shouldTouch: true };
        participantForm.setValue(
          'civility',
          participant.civility === 'Mr' ? civilityOptions[0].value : civilityOptions[1].value,
          formStateOptions
        );
        participantForm.setValue('firstName', participant.firstName, formStateOptions);
        participantForm.setValue('lastName', participant.name, formStateOptions);
        participantForm.setValue('birthDate', getValidDate(participant.birthDate), formStateOptions);
      }
    } finally {
      setHasSearchParticipant(true);
    }
  };

  const onAddNewParticipant = async () => {
    if (isEmpty(passengerCategory)) return;

    // Reset invalid participant age message
    setInvalidParticipantAge(null);

    const { serialNumber, firstName, lastName, birthDate, civility } = participantForm.getValues();

    if (!isParticipantAgeValid()) {
      setInvalidParticipantAge(getParticipantAgeErrorMessage());
    } else {
      const { data } =
        (await addParticipantCall({
          service: 'castelis',
          path: `/users/rightHolders/${currentUser.serialNumber}/rightHolder/`,
          method: 'post',
          query: {
            firstName,
            name: lastName,
            birthDate: format(birthDate, 'dd/MM/yyyy'),
            civility,
            /*
              sameAddress and linkType are required to add new rightHolder.
              By default the linkType will be 'Enfant' for the new rightHolder/participant
            */
            sameAddress: true,
            linkType: 'Enfant',
            ...(serialNumber &&
              hasSearchParticipant &&
              !isEmpty(participantBySerialNumber?.rightHolders) && { rightHolderId: serialNumber })
          }
        })) || {};
      if (data?.status === 200 && onAddSuccess) {
        onAddSuccess();
      }
    }
  };

  /* Verify participant's age based on minOld and maxOld criteria */
  const isParticipantAgeValid = () => {
    const { birthDate } = participantForm.getValues();
    const returnDateObj = new Date(storedReturnDate);

    let age = getYear(returnDateObj) - getYear(birthDate);
    const month = getMonth(returnDateObj) - getMonth(birthDate);
    if (month < 0 || (month === 0 && getDate(returnDateObj) < getDate(birthDate))) {
      age--;
    }

    /*
     * Convert minOld and maxOld to integers.
     * If maxOld is an empty string, set it to a high number to imply no upper limit
     */
    const minAge = parseInt(passengerCategory.minOld, 10);
    const maxAge = passengerCategory.maxOld === '' ? 999 : parseInt(passengerCategory.maxOld, 10);

    return age >= minAge && age < maxAge;
  };

  const getParticipantAgeErrorMessage = () => {
    const maxOld = toNumber(passengerCategory.maxOld);
    const minOld = toNumber(passengerCategory.minOld);

    if (minOld && maxOld) {
      return t('purchase:configuration.invalidParticipant.minMaxAge', { minAge: minOld, maxAge: maxOld - 1 });
    } else if (minOld && !maxOld) {
      return t('purchase:configuration.invalidParticipant.minAge', { minAge: minOld });
    } else if (!minOld && maxOld) {
      return t('purchase:configuration.invalidParticipant.maxAge', { maxAge: maxOld });
    }
  };

  return (
    <FormProvider {...participantForm}>
      <form onSubmit={participantForm.handleSubmit(onAddNewParticipant)}>
        {/* SERIAL NUMBER */}
        <div className="grid grid-cols-4 items-center gap-x-4 md:grid-cols-3">
          <p className="col-span-4 text-gray-600 text-sm uppercase md:col-span-1">{t('form:serialNumber')}</p>
          <FormInput
            name="serialNumber"
            className="col-span-2 md:col-span-1"
            onBlur={(value) => participantForm.setValue('serialNumber', processString(value))}
          />
          <button
            type="button"
            className="col-span-2 btn secondary text-sm md:text-base md:col-span-1 md:w-fit"
            onClick={findParticipantBySerialNumber}
            disabled={isEmpty(searchParticipant) || findParticipantLoading}
          >
            {findParticipantLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
            {t('buttons.search')}
          </button>

          {/* Display error message if participant not found */}
          <p className="col-span-4 min-h-5 md:col-span-3 md:col-start-2">
            {!findParticipantLoading && hasSearchParticipant && isEmpty(participantBySerialNumber?.serialNumber) && (
              <span className="text-red-500 mb-2 block">{t('purchase:configuration.participantNotFound')}</span>
            )}
          </p>
        </div>

        {/* CIVILITY */}
        <div className="grid grid-cols-4 items-center gap-x-4 mb-4 md:grid-cols-3">
          <p className="col-span-4 text-gray-600 text-sm uppercase mb-2 md:mb-0 md:col-span-1">
            {t('form:civility.label')}
          </p>

          <RadioGroup
            radioOptions={civilityOptions}
            row
            rules={{ required: true }}
            className="col-span-4 md:col-span-2 -ml-4"
          />
        </div>

        {/* LASTNAME & FIRSTNAME */}
        <div className="grid grid-cols-4 items-center gap-x-4 mb-6 md:grid-cols-3">
          <p className="hidden text-gray-600 text-sm uppercase md:col-span-1 md:flex md:flex-col">
            <span className="block">{t('form:lastname')}</span>
            <span className="block">{t('form:firstname')}</span>
          </p>
          <p className="col-span-2 text-gray-600 uppercase text-sm md:hidden">{t('form:lastname')}</p>
          <p className="col-span-2 text-gray-600 uppercase text-sm md:hidden">{t('form:firstname')}</p>
          <FormInput
            name="lastName"
            placeholder={t('form:lastname')}
            rules={{ required: true }}
            className="col-span-2 md:col-span-1"
            onBlur={(value) => participantForm.setValue('lastName', processString(value))}
          />
          <FormInput
            name="firstName"
            placeholder={t('form:firstname')}
            rules={{ required: true }}
            className="col-span-2 md:col-span-1"
            onBlur={(value) => participantForm.setValue('firstName', processString(value))}
          />
        </div>

        {/* BIRTHDATE */}
        <div className="grid grid-cols-4 items-center gap-x-4 mb-6 md:grid-cols-3">
          <p className="col-span-4 text-gray-600 text-sm uppercase mb-2 md:mb-0 md:col-span-1">{t('form:birthdate')}</p>

          <Controller
            name="birthDate"
            control={participantForm.control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                selected={value}
                onChange={onChange}
                name="bithDate"
                placeholder="jj/mm/aaaa"
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={40}
                containerClass="col-span-4 md:col-span-2"
                openToDate={new Date(value || '1970/01/01')}
                maxDate={new Date()}
              />
            )}
          />
        </div>

        <button
          type="submit"
          className="btn ml-auto"
          disabled={!participantForm.formState.isValid || addParticipantLoading}
        >
          {addParticipantLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
          {t('buttons.continue')}
        </button>

        {/* Display error message if participant age doesn't correspond to the category of ParticipantCard */}
        {invalidParticipantAge && (
          <p className="w-full text-right mt-4">
            <span className="text-red-500">{invalidParticipantAge}</span>
          </p>
        )}
      </form>
    </FormProvider>
  );
};

AddParticipantForm.propTypes = {
  passengerCategory: shape({
    label: string,
    maxOld: string,
    minOld: string,
    type: string
  }),
  onAddSuccess: func.isRequired
};

export default AddParticipantForm;
